import { useEffect, useMemo, useState } from "react";
import { Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import type { CleaningTaskWithRelations } from "@/types/cleaning";
import {
  type ReservationDateRow,
  type TurnoverWindow,
  chipClassName,
  formatClock12,
  resolveTurnoverWindow,
} from "@/lib/turnoverWindow";
import { useUpdateCleaningTask } from "@/hooks/useEditCleaningTask";
import { useCreateSingleTask } from "@/hooks/useCleaningTasks";
import { toast } from "@/hooks/use-toast";
import { USER_PREFIX } from "@/hooks/usePropertyAssignableMembers";

export type ScheduleAssignOption = {
  value: string;
  name: string;
  avatarUrl?: string | null;
  /** Secondary line under name (e.g. Team admin) */
  role?: string | null;
};

export type ScheduleTurnoverSelection = {
  /** Cleaning task when one exists */
  task: CleaningTaskWithRelations | null;
  /** Departing reservation (checkout side) */
  departingReservationId: string;
  checkoutDate: string;
};

function initialsOf(name: string | null | undefined): string {
  const t = (name || "").trim();
  if (!t) return "?";
  const parts = t.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0] ?? ""}${parts[1]![0] ?? ""}`.toUpperCase();
}

function assigneeValueFromTask(
  task: CleaningTaskWithRelations | null,
  userId: string | null | undefined,
): string {
  if (!task) return "unassigned";
  const t = task as {
    assigned_type?: string | null;
    cleaner_id?: string | null;
    assignee_user_id?: string | null;
  };
  if (t.assigned_type === "owner" || (t.assignee_user_id && userId && t.assignee_user_id === userId)) {
    return "self";
  }
  if (t.assignee_user_id) return `${USER_PREFIX}${t.assignee_user_id}`;
  if (t.cleaner_id) return t.cleaner_id;
  return "unassigned";
}

function GuestRow({
  name,
  photoUrl,
  line,
  empty,
}: {
  name: string | null;
  photoUrl: string | null;
  line: string | null;
  empty?: boolean;
}) {
  if (empty) {
    return (
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 shrink-0 rounded-full border-[1.5px] border-dashed border-[#d7e0e6]" />
        <div className="min-w-0 text-[15px] font-medium text-[#8494a2]">No next guest</div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3">
      <Avatar className="h-11 w-11 shrink-0">
        {photoUrl ? <AvatarImage src={photoUrl} alt="" referrerPolicy="no-referrer" /> : null}
        <AvatarFallback className="bg-[#e6edf1] text-[14px] font-bold text-[#475569]">
          {initialsOf(name)}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1 flex flex-col gap-0.5">
        <div className="truncate text-[15.5px] font-bold tracking-[-0.2px] text-[#0f1720]">
          {name?.trim() || "Guest"}
        </div>
        {line ? <div className="text-[13px] font-medium text-[#8494a2]">{line}</div> : null}
      </div>
    </div>
  );
}

function guestLine(opts: {
  verb: "out" | "in";
  dateLabel: string | null;
  clock: string | null;
  compact?: boolean;
}): string {
  const clock = formatClock12(opts.clock);
  const verb = opts.verb === "out" ? "Checks out" : "Checks in";
  if (opts.compact) {
    if (clock) return `${verb} ${clock}`;
    return verb;
  }
  if (opts.dateLabel && clock) return `${verb} ${opts.dateLabel} · ${clock}`;
  if (opts.dateLabel) return `${verb} ${opts.dateLabel}`;
  if (clock) return `${verb} ${clock}`;
  return verb;
}

export function ScheduleTurnoverDetail({
  selection,
  reservations,
  propertyId,
  defaultChecklistId,
  assignOptions,
  assigneeLabel,
  isAssigned,
  currentUserId,
  canAssign,
  compactDates,
  bare,
  onClose,
  onAssigned,
}: {
  selection: ScheduleTurnoverSelection;
  reservations: ReservationDateRow[];
  propertyId: string;
  defaultChecklistId: string | null;
  assignOptions: ScheduleAssignOption[];
  assigneeLabel: string;
  isAssigned: boolean;
  currentUserId?: string | null;
  canAssign: boolean;
  /** Phone sheet: shorter Out/In lines */
  compactDates?: boolean;
  /** Phone drawer: no outer card chrome */
  bare?: boolean;
  onClose: () => void;
  onAssigned?: () => void;
}) {
  const updateTask = useUpdateCleaningTask();
  const createTask = useCreateSingleTask();
  const [assignOpen, setAssignOpen] = useState(false);
  const [pick, setPick] = useState(() => assigneeValueFromTask(selection.task, currentUserId));
  const saving = updateTask.isPending || createTask.isPending;

  useEffect(() => {
    setAssignOpen(false);
    let next = assigneeValueFromTask(selection.task, currentUserId);
    // Owner may be stored as "self" but listed as team user:uuid after dedupe
    if (next === "self" && currentUserId) {
      const asTeam = `${USER_PREFIX}${currentUserId}`;
      if (assignOptions.some((o) => o.value === asTeam)) next = asTeam;
    }
    setPick(next);
  }, [selection.task?.id, selection.departingReservationId, currentUserId, assignOptions]);

  const windowInfo: TurnoverWindow = useMemo(
    () =>
      resolveTurnoverWindow({
        checkoutDate: selection.checkoutDate,
        reservations,
        departingReservationId: selection.departingReservationId,
        taskStatus: selection.task?.status,
        isTest: !!(selection.task as { is_test?: boolean } | null)?.is_test,
      }),
    [selection, reservations],
  );

  const title = windowInfo.sameDay
    ? `Same day · ${windowInfo.label.replace(/^Same day ·\s*/i, "")}`
    : windowInfo.label;

  /** Primary chip matches list view (Waiting on guest, Same day, Open, …) */
  const primaryChip = windowInfo.chip;
  /** Quiet secondary note under guests (Waiting · past … / Guest left / Updated) */
  const statusNote = windowInfo.statusNote;

  const outDateFmt = (() => {
    try {
      const d = selection.checkoutDate.slice(0, 10);
      const [y, m, day] = d.split("-").map(Number);
      const dt = new Date(y!, m! - 1, day!);
      return dt.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
    } catch {
      return selection.checkoutDate;
    }
  })();
  const inDateFmt = windowInfo.nextCheckInDate
    ? (() => {
        try {
          const d = windowInfo.nextCheckInDate.slice(0, 10);
          const [y, m, day] = d.split("-").map(Number);
          const dt = new Date(y!, m! - 1, day!);
          return dt.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
        } catch {
          return windowInfo.nextCheckInDate;
        }
      })()
    : null;

  const handleSave = async () => {
    if (!canAssign) return;
    const isSelf = pick === "self";
    const isTeam = pick.startsWith(USER_PREFIX);
    const assigneeUserId = isSelf ? currentUserId ?? null : isTeam ? pick.slice(USER_PREFIX.length) : null;
    const cleanerId = pick === "unassigned" || isSelf || isTeam ? null : pick;
    const assignedType: "owner" | "cleaner" | "unassigned" | "team" = isSelf
      ? "owner"
      : assigneeUserId
        ? "team"
        : cleanerId
          ? "cleaner"
          : "unassigned";

    try {
      if (selection.task) {
        await updateTask.mutateAsync({
          taskId: selection.task.id,
          propertyId,
          cleanerId,
          assigneeUserId,
          assignedType,
          checklistId: selection.task.checklist_id ?? defaultChecklistId,
          scheduledDate: selection.task.scheduled_date,
          notes: selection.task.notes ?? null,
          status: selection.task.status === "in_progress" ? "in_progress" : selection.task.status,
        });
      } else {
        await createTask.mutateAsync({
          propertyId,
          reservationId: selection.departingReservationId,
          checklistId: defaultChecklistId,
          cleanerId,
          assigneeUserId,
          assignedType,
          scheduledDate: selection.checkoutDate.slice(0, 10),
        });
      }
      toast({ title: "Saved" });
      setAssignOpen(false);
      onAssigned?.();
    } catch (err) {
      toast({
        title: "Couldn’t save",
        description: err instanceof Error ? err.message : undefined,
        variant: "destructive",
      });
    }
  };

  return (
    <div
      className={cn(
        "flex flex-col overflow-hidden bg-white",
        !bare && "rounded-[16px] border border-[#e6edf1]",
      )}
      data-tour="tour-schedule-turnover-detail"
    >
      <div className="flex items-start gap-3 border-b border-[#eef3f6] px-5 pb-4 pt-[18px]">
        <div className="min-w-0 flex-1 flex flex-col gap-2">
          <div className="text-[19px] font-bold tracking-[-0.3px] text-[#0f1720]">{title}</div>
          <span
            className={cn(
              "inline-flex w-fit items-center rounded-full px-3 py-1 text-[11.5px] font-bold tracking-[0.3px]",
              chipClassName(primaryChip),
            )}
          >
            {primaryChip}
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[9px] text-[#64748b] hover:bg-[#f1f5f7]"
          aria-label="Close"
        >
          <X className="h-4 w-4" strokeWidth={2.3} />
        </button>
      </div>

      <div className="flex flex-col gap-[18px] border-b border-[#eef3f6] px-5 py-[18px]">
        <GuestRow
          name={windowInfo.outGuestName}
          photoUrl={windowInfo.outGuestPhotoUrl}
          line={guestLine({
            verb: "out",
            dateLabel: compactDates ? null : outDateFmt,
            clock: windowInfo.departingCheckOutTime,
            compact: compactDates,
          })}
        />
        {windowInfo.openEnded ? (
          <GuestRow name={null} photoUrl={null} line={null} empty />
        ) : (
          <GuestRow
            name={windowInfo.inGuestName}
            photoUrl={windowInfo.inGuestPhotoUrl}
            line={guestLine({
              verb: "in",
              dateLabel: compactDates ? null : inDateFmt,
              clock: windowInfo.arrivingCheckInTime,
              compact: compactDates,
            })}
          />
        )}
        {statusNote ? (
          <div className="text-[12.5px] font-bold text-[#9a6700]">{statusNote}</div>
        ) : null}
      </div>

      <div className="flex flex-col gap-3 px-5 pb-5 pt-[18px]">
        {!assignOpen ? (
          <>
            <div
              className={cn(
                "text-[11px] font-bold tracking-[0.8px]",
                isAssigned ? "text-[#8494a2]" : "text-[#8a5a08]",
              )}
            >
              {isAssigned ? "ASSIGNED" : "UNASSIGNED"}
            </div>
            {isAssigned ? (
              <div className="flex items-center gap-3">
                <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-[#e2eeed] text-[13px] font-bold text-[#15776d]">
                  {initialsOf(assigneeLabel)}
                </div>
                <div className="min-w-0 flex-1 truncate text-[15px] font-bold text-[#0f1720]">{assigneeLabel}</div>
                {canAssign ? (
                  <button
                    type="button"
                    onClick={() => setAssignOpen(true)}
                    className="inline-flex h-[34px] items-center rounded-[10px] border border-[#e6edf1] px-3.5 text-[13px] font-bold text-[#475569] hover:border-[#24a89a] hover:text-[#15776d]"
                  >
                    Assign
                  </button>
                ) : null}
              </div>
            ) : canAssign ? (
              <button
                type="button"
                onClick={() => setAssignOpen(true)}
                className="flex h-11 items-center justify-center rounded-[12px] bg-[#24a89a] text-[15px] font-bold text-white hover:bg-[#1b8378]"
              >
                Assign
              </button>
            ) : (
              <div className="text-[14px] font-medium text-[#8494a2]">Unassigned</div>
            )}
          </>
        ) : (
          <>
            <div className="text-[11px] font-bold tracking-[0.8px] text-[#8a5a08]">UNASSIGNED</div>
            <div className="overflow-hidden rounded-[12px] border border-[#e6edf1]">
              {assignOptions.map((opt, i) => {
                const selected = pick === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setPick(opt.value)}
                    className={cn(
                      "flex w-full items-center gap-3 px-[15px] py-[13px] text-left hover:bg-[#f8fbfc]",
                      i < assignOptions.length - 1 && "border-b border-[#f1f5f7]",
                    )}
                  >
                    <span
                      className={cn(
                        "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2",
                        selected ? "border-[#24a89a]" : "border-[#cdd8df]",
                      )}
                    >
                      {selected ? <span className="h-2.5 w-2.5 rounded-full bg-[#24a89a]" /> : null}
                    </span>
                    {opt.value !== "unassigned" ? (
                      <Avatar className="h-8 w-8 shrink-0">
                        {opt.avatarUrl ? <AvatarImage src={opt.avatarUrl} alt="" /> : null}
                        <AvatarFallback className="bg-[#e2eeed] text-[11px] font-bold text-[#15776d]">
                          {initialsOf(opt.name)}
                        </AvatarFallback>
                      </Avatar>
                    ) : (
                      <span className="h-8 w-8 shrink-0 rounded-full border border-dashed border-[#d7e0e6]" />
                    )}
                    <span className="min-w-0 flex-1">
                      <span
                        className={cn(
                          "block truncate text-[14.5px]",
                          selected ? "font-bold text-[#0f1720]" : "font-medium text-[#475569]",
                        )}
                      >
                        {opt.name}
                      </span>
                      {opt.role ? (
                        <span className="block truncate text-[12px] font-medium text-[#8494a2]">{opt.role}</span>
                      ) : null}
                    </span>
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              disabled={saving}
              onClick={() => void handleSave()}
              className="flex h-11 items-center justify-center gap-2 rounded-[12px] bg-[#24a89a] text-[15px] font-bold text-white hover:bg-[#1b8378] disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Save
            </button>
          </>
        )}
      </div>
    </div>
  );
}
